import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHlsPlayer } from '@/hooks/useHlsPlayer';
import { useExtraBufferPref, BufferControl } from '@/components/BufferControl';
import { LiveTvSourceMenu } from '@/components/LiveTvSourceMenu';
import { buildLiveTvPlaylistUrl, livetvApi } from '@/services/livetvApi';
import type { LiveTvChannel, LiveTvSource } from '@/types/livetv';

interface Props {
  channelId: string;
}

/**
 * Live TV channel player. Reuses useHlsPlayer (live mode).
 *
 * Two axes:
 *  - Language toggle: same logical channel from several catalogs/countries is
 *    one card with `sources[]`; the viewer picks a language, which changes which
 *    source id (`activeId`) plays. Default = the source matching the UI language.
 *  - Client-direct playback (#2): iptv sources the backend flags as header-free
 *    arrive with a `directUrl`, tried first (residential IP → geo-bypass) with
 *    proxy fallback. Both axes compose: direct/proxy is resolved per active source.
 */
type PlaybackMode = 'direct' | 'proxy';

/** Pick the source matching the UI language, else the primary (first). */
function defaultSourceId(channelId: string, sources: LiveTvSource[], uiLang: string): string {
  if (sources.length === 0) return channelId;
  const langName = uiLang.startsWith('es') ? 'Spanish' : uiLang.startsWith('en') ? 'English' : null;
  const match = langName ? sources.find((s) => s.language === langName) : undefined;
  return (match ?? sources[0]).id;
}

export function WatchLiveTV({ channelId }: Props): JSX.Element {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  // The canonical card (its `sources` drive the language toggle + the title).
  const [card, setCard] = useState<LiveTvChannel | null>(null);
  // The currently-selected source id — what actually plays. Defaults to the
  // canonical id until the card loads and we can match the UI language.
  const [activeId, setActiveId] = useState<string>(channelId);
  // Playback info for the active source (name/directUrl).
  const [active, setActive] = useState<LiveTvChannel | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [mode, setMode] = useState<PlaybackMode>('proxy');
  const directAttemptedRef = useRef(false);

  // Reset everything when the (canonical) channel changes.
  useEffect(() => {
    setCard(null);
    setActive(null);
    setActiveId(channelId);
  }, [channelId]);

  // Fetch the canonical card → sources + default language selection.
  useEffect(() => {
    let cancelled = false;
    livetvApi
      .getChannel(channelId)
      .then((res) => {
        if (cancelled) return;
        setCard(res.channel);
        setActiveId(defaultSourceId(channelId, res.channel.sources ?? [], i18n.language ?? 'en'));
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (err.status === 404) navigate('/live-tv', { replace: true });
      });
    return () => { cancelled = true; };
  }, [channelId, navigate, i18n.language]);

  // Fetch the active source's playback info and (re)arm client-direct for it.
  useEffect(() => {
    let cancelled = false;
    setMode('proxy');
    directAttemptedRef.current = false;
    livetvApi
      .getChannel(activeId)
      .then((res) => {
        if (cancelled) return;
        setActive(res.channel);
        if (res.channel.directUrl && !directAttemptedRef.current) {
          directAttemptedRef.current = true;
          setMode('direct');
        }
      })
      .catch(() => { /* keep proxy; a failed detail fetch still plays via proxy */ });
    return () => { cancelled = true; };
  }, [activeId]);

  const directUrl = active?.directUrl;
  const useDirect = mode === 'direct' && !!directUrl;
  const playlistUrl = useMemo(
    () => (useDirect ? (directUrl as string) : buildLiveTvPlaylistUrl(activeId)),
    [useDirect, directUrl, activeId],
  );
  const { value: extraBuffer, setValue: setExtraBuffer } = useExtraBufferPref();

  const { fatal } = useHlsPlayer({
    playlistUrl,
    videoRef,
    retryToken,
    key: `livetv|${activeId}|${useDirect ? 'direct' : 'proxy'}`,
    live: true,
    extraBuffer,
    p2pMode: null,
    crossOriginNoCreds: useDirect,
  });

  // Direct attempt failed → fall back to the server proxy (geo-bypass safety net).
  useEffect(() => {
    if (fatal && useDirect) setMode('proxy');
  }, [fatal, useDirect]);

  const displayFatal = fatal && !useDirect ? fatal : null;
  const sources = card?.sources ?? [];

  return (
    <main className="watch-page watch-page--sports">
      <header className="watch-page__title">
        <h1>{card?.name ?? t('watch.liveTv.channelFallback', { id: channelId })}</h1>
        <span className="time-pill time-pill--live">{t('watch.live')}</span>
        <LiveTvSourceMenu
          sources={sources}
          activeId={activeId}
          onSelect={(s) => setActiveId(s.id)}
        />
      </header>

      <div className="watch-stage">
        <video ref={videoRef} controls autoPlay playsInline className="watch-stage__video" />
        {displayFatal && (
          <div className="watch-stage__fatal" role="alert" data-testid="watch-livetv-fatal">
            <div>{t('watch.liveTv.channelUnavailable', { fatal: displayFatal })}</div>
            <button type="button" onClick={() => setRetryToken((n) => n + 1)}>{t('common.retry')}</button>
            <button type="button" onClick={() => navigate('/live-tv')}>{t('watch.liveTv.back')}</button>
          </div>
        )}
      </div>

      <BufferControl value={extraBuffer} onChange={setExtraBuffer} />
    </main>
  );
}
